import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertTriangle, ExternalLink, GitMerge, Check } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Fase 8.3 · Dialog "Possibile duplicato" mostrato PRIMA di salvare quando
 * il preflight (o la risposta 409 del server) trova candidati sospetti.
 *
 * Scelte non forzanti (il sistema non unisce/blocca mai automaticamente):
 *  - "Procedi comunque": salva il vino, i sospetti vengono registrati e
 *    l'admin li ritroverà nel tab Segnalazioni.
 *  - "Annulla": chiude il dialog senza salvare.
 *  - Per ogni candidato: link "Apri scheda" per verificare manualmente.
 */
export default function DuplicateSuspectDialog({
    open, onOpenChange, candidates = [], onProceed, onCancel,
}) {
    if (!candidates.length) return null;
    return (
        <AlertDialog open={open} onOpenChange={onOpenChange}>
            <AlertDialogContent className="max-w-2xl" data-testid="duplicate-dialog">
                <AlertDialogHeader>
                    <AlertDialogTitle className="flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-[#B87500]" strokeWidth={1.5} />
                        Possibile duplicato — vuoi procedere?
                    </AlertDialogTitle>
                    <AlertDialogDescription asChild>
                        <div className="text-sm text-[hsl(var(--muted-foreground))]">
                            Il sistema ha rilevato <b>{candidates.length}</b>{" "}
                            {candidates.length === 1 ? "possibile duplicato" : "possibili duplicati"} in catalogo.
                            Nessuna decisione automatica: puoi scegliere cosa fare.
                        </div>
                    </AlertDialogDescription>
                </AlertDialogHeader>

                <ul className="my-2 max-h-64 overflow-y-auto border border-[hsl(var(--border))] rounded-sm bg-white">
                    {candidates.slice(0, 10).map((c, idx) => (
                        <li
                            key={`${c.wine_id}-${idx}`}
                            className="p-3 border-b border-[hsl(var(--border))] last:border-b-0 flex items-start gap-3"
                            data-testid={`dup-dialog-row-${idx}`}
                        >
                            <MatchPill match_type={c.match_type} similarity={c.similarity} />
                            <div className="flex-1 min-w-0">
                                <div className="text-sm">{c.name_existing || "—"}</div>
                                <div className="text-xs text-[hsl(var(--muted-foreground))]">
                                    {c.producer_existing || "—"}
                                    {c.vintage_existing ? ` · ${c.vintage_existing}` : ""}
                                </div>
                                <div className="text-[11px] text-[hsl(var(--muted-foreground))] italic mt-1">
                                    {c.reason}
                                </div>
                            </div>
                            <Link
                                to={`/admin/vini/${c.wine_id}`}
                                target="_blank"
                                rel="noopener"
                                className="text-xs text-[hsl(var(--primary))] hover:underline inline-flex items-center gap-1 shrink-0"
                                data-testid={`dup-dialog-open-${idx}`}
                            >
                                <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
                                Scheda
                            </Link>
                        </li>
                    ))}
                </ul>

                <div className="text-xs text-[hsl(var(--muted-foreground))]">
                    <b>È lo stesso vino?</b> Salvalo comunque e poi <GitMerge className="inline w-3 h-3 -mt-0.5" />{" "}
                    aprilo come proposta di merge dal tab <i>Segnalazioni</i>.
                </div>

                <AlertDialogFooter>
                    <AlertDialogCancel onClick={onCancel} data-testid="dup-dialog-cancel">
                        Annulla salvataggio
                    </AlertDialogCancel>
                    <AlertDialogAction
                        onClick={onProceed}
                        className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90"
                        data-testid="dup-dialog-proceed"
                    >
                        <Check className="w-4 h-4" strokeWidth={1.5} />
                        Procedi comunque
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}

function MatchPill({ match_type, similarity }) {
    const map = {
        exact: "bg-[#5B1C2C] text-white border-[#5B1C2C]",
        edit_distance: "bg-[#F4E9DC] text-[#5B1C2C] border-[#D9B98F]",
        partial: "bg-[#F2EDE4] text-[hsl(var(--muted-foreground))] border-[#D9B98F]",
    };
    const label = match_type === "exact" ? "IDENTICO"
        : match_type === "edit_distance" ? "SIMILE" : "SIMILE";
    return (
        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border shrink-0 ${map[match_type] || map.partial}`}>
            {label} {Math.round((similarity || 0) * 100)}%
        </span>
    );
}
